import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { LanguagePicker, LegalDocument } from "../components/LegalDocument";
import { PageChrome } from "../components/PageChrome";
import {
  legalPath,
  loadLegalBundle,
  resolveLegalLang,
} from "../lib/legalDocs/registry";
import { alternates, canonical, documentHeaders } from "../lib/seo";

/**
 * The Terms of Service, in whichever of the nine app languages `?lang=` asks
 * for. Same URL and same per-language loading as `/privacy` — see the note
 * there.
 *
 * `#community` must keep working in every language: it is the EULA row in the
 * app's community consent screen, and the section ids are shared across the
 * translations precisely so that link does not have to change.
 */
export const Route = createFileRoute("/terms")({
  validateSearch: (search: Record<string, unknown>): { lang?: string } => ({
    lang: typeof search.lang === "string" ? search.lang : undefined,
  }),
  loaderDeps: ({ search }) => ({ lang: resolveLegalLang(search.lang) }),
  loader: ({ deps }) => loadLegalBundle(deps.lang),
  headers: documentHeaders,
  // Canonical and hreflang both come from `legalPath` — see the note on
  // `/privacy` for the cross-locale canonical this replaced.
  head: ({ match }) => ({
    meta: [{ title: "Terms of Service — Bible Shield" }],
    links: [
      {
        rel: "canonical",
        href: canonical(legalPath("/terms", match.loaderDeps.lang)),
      },
      ...alternates((code) => legalPath("/terms", code)),
    ],
  }),
  component: Terms,
});

function Terms() {
  const { lang } = Route.useLoaderDeps();
  const bundle = Route.useLoaderData();

  // Page content renders on the client, so the browser's native #hash scroll can
  // fire before the target exists. Re-run it on mount so a deep link such as
  // /terms#community (the in-app EULA row) lands on the right section.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <PageChrome title={bundle.terms.title}>
      <LanguagePicker
        path="/terms"
        current={lang}
        label={bundle.ui.languageLabel}
      />
      <LegalDocument doc={bundle.terms} bundle={bundle} lang={lang} />
    </PageChrome>
  );
}
